/* ==========================================================================
   Start Nutra PDP behaviour
   --------------------------------------------------------------------------
   SnGallery       reflect the in-view image as the active thumb/dot (the
                   click-to-scroll anchor interaction itself is untouched)
   SnQtyStepper    +/- stepper around a <input type="number">
   SnSubToggle     one-time vs subscribe radios: enables the selling_plan and
                   quantity=1 form inputs while Subscribe is checked
   SnOrder         the purchase card's numbers: the total row, the button
                   text, the microcopy state, the "Your order" panel, the
                   add-on cards below the card, and the Subscribe rule (a
                   product on a different plan blocks Subscribe)
   SnPdpBuy        form submit: a combo page adds its components, and a
                   single product with add-ons adds them all, in one
                   /cart/add.js request. Any other add posts natively.
   ========================================================================== */

const SN_DOT = String.fromCharCode(183);
const SN_TIMES = String.fromCharCode(215);

function snIsSubscribe(card) {
  const selected = card.querySelector('input[type="radio"][name="sn-purchase-mode"]:checked');
  return !!selected && selected.value === 'subscribe';
}

function snFormatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

// "Add {n} to bag {price}" with {n} and {price} filled in; unknown tokens stay.
function snFill(template, values) {
  return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
    return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
  });
}

// The plan's delivery text as a short cadence: "Deliver every 8 weeks"
// becomes "Every 8 weeks".
function snCadence(text) {
  const t = String(text || '').trim().replace(/^deliver\s+/i, '');
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '';
}

// One product's numbers, read from the data-* attributes the markup carries
// (a "Your order" row or an add-on card). All money is in pence.
function snProductData(el) {
  const d = el.dataset;
  const sub = d.sub === undefined || d.sub === '' ? NaN : parseInt(d.sub, 10);
  return {
    id: Number(d.id),
    name: d.name || '',
    full: parseInt(d.full, 10) || 0,
    paired: parseInt(d.paired, 10) || 0,
    sub: sub,
    planId: d.planId || '',
    planText: d.planText || ''
  };
}

class SnGallery {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('.sn-pdp-gallery__main');
    this.slides = root.querySelectorAll('[data-sn-slide-index]');
    this.thumbs = root.querySelectorAll('[data-sn-thumb-index]');
    this.dots = root.querySelectorAll('[data-sn-dot-index]');
    // Single image: nothing to track. Thumbs/dots only render with 2+ images.
    if (!this.viewport || this.slides.length < 2) return;
    this.setActive(1);
    this.observe();
  }

  observe() {
    // Watch which slide sits in the scroll viewport and mirror it onto the
    // matching thumb (aria-current) and dot (.is-active). No animation here,
    // so there is nothing to gate behind prefers-reduced-motion.
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = parseInt(entry.target.dataset.snSlideIndex, 10);
        if (!isNaN(idx)) this.setActive(idx);
      });
    }, { root: this.viewport, threshold: 0.6 });

    this.slides.forEach((slide) => obs.observe(slide));
  }

  setActive(idx) {
    this.thumbs.forEach((thumb) => {
      if (parseInt(thumb.dataset.snThumbIndex, 10) === idx) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    this.dots.forEach((dot) => {
      dot.classList.toggle('is-active', parseInt(dot.dataset.snDotIndex, 10) === idx);
    });
  }
}

class SnQtyStepper {
  constructor(root) {
    this.root = root;
    this.input = root.querySelector('.sn-qty__input');
    this.minus = root.querySelector('[data-sn-qty="decrease"]');
    this.plus = root.querySelector('[data-sn-qty="increase"]');
    if (!this.input || !this.minus || !this.plus) return;
    this.min = parseInt(this.input.min || '1', 10);
    this.max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    this.bind();
    this.syncDisabled();
  }

  bind() {
    this.minus.addEventListener('click', () => this.change(-1));
    this.plus.addEventListener('click', () => this.change(1));
    this.input.addEventListener('change', () => this.clamp());
  }

  change(delta) {
    const current = parseInt(this.input.value || this.min, 10);
    const next = Math.max(this.min, Math.min(this.max, current + delta));
    this.input.value = String(next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncDisabled();
  }

  clamp() {
    let v = parseInt(this.input.value, 10);
    if (isNaN(v)) v = this.min;
    v = Math.max(this.min, Math.min(this.max, v));
    this.input.value = String(v);
    this.syncDisabled();
  }

  syncDisabled() {
    const v = parseInt(this.input.value, 10);
    this.minus.disabled = v <= this.min;
    this.plus.disabled = v >= this.max;
  }
}

class SnSubToggle {
  constructor(root) {
    this.root = root;
    // The radios only exist when the product has a plan and the Subscribe
    // switch is on, so with one option there is nothing to wire up.
    this.radios = root.querySelectorAll('input[type="radio"][name="sn-purchase-mode"]');
    if (!this.radios.length) return;
    // Form inputs that make the purchase a real subscription. Both are
    // rendered disabled, so without this script the form posts a one-time
    // purchase. They are only enabled while Subscribe is checked.
    const form = root.closest('form');
    this.sellingPlan = form ? form.querySelector('[data-sn-selling-plan]') : null;
    this.subscribeQty = form ? form.querySelector('[data-sn-subscribe-qty]') : null;
    this.qtyInput = root.querySelector('.sn-qty__input');
    this.bind();
    this.sync();
  }

  bind() {
    this.radios.forEach((r) => {
      r.addEventListener('change', () => this.sync());
    });
    // Back/forward restores can bring the radios back in a state other than
    // the one the markup shipped with, so re-read them.
    window.addEventListener('pageshow', () => this.sync());
  }

  isSubscribe() {
    return snIsSubscribe(this.root);
  }

  sync() {
    const subscribing = this.isSubscribe();
    // Subscribe posts the plan and a quantity of 1 (one unit per delivery).
    // One-time posts neither: the stepper's own quantity goes through. The
    // stepper input is disabled in subscribe mode so only one quantity is
    // sent, and its value is untouched for when the customer switches back.
    if (this.sellingPlan) this.sellingPlan.disabled = !subscribing;
    if (this.subscribeQty) this.subscribeQty.disabled = !subscribing;
    if (this.subscribeQty && this.qtyInput) this.qtyInput.disabled = subscribing;
    // The numbers on the card follow the mode too.
    if (this.root.snOrder) this.root.snOrder.sync();
  }
}

/* The purchase card's numbers, in one place.

   The order is the products on this page's card (this product, or a combo's
   products: the "base" rows, rendered by Liquid) plus the add-ons the
   customer has moved in from the cards below. Everything shown is worked out
   from the data-* attributes on those rows and cards, in pence, by the same
   rules the bag applies:
     One-time  Two or more units: every line takes the Multi-buy discount,
               rounded down to the penny per line (a line's `paired` price
               comes from snippets/sn-multibuy-price.liquid; this product's
               own line, which can have a quantity, is worked out here the
               same way). One unit: full price.
     Subscribe Every line is that product's selling plan allocation price.
   The saving is always subtotal minus total, never a separate sum.

   Subscribe rule: two products can be subscribed together only when both
   have a plan and the plans' delivery text (plan.options.first.value) is the
   same. An add-on that fails the rule can still be bought one-time, but while
   one is in the order the Subscribe tab is disabled. */
class SnOrder {
  constructor(card) {
    this.card = card;
    this.panel = card.querySelector('[data-sn-order]');
    this.list = card.querySelector('[data-sn-order-list]');
    this.baseRows = Array.prototype.slice.call(card.querySelectorAll('[data-sn-row]'));
    if (!this.panel || !this.list || !this.baseRows.length) return;
    const scope = card.closest('.sn-pdp-hero__content') || document;
    this.suggest = scope.querySelector('[data-sn-suggest]');
    this.cards = this.suggest ? Array.prototype.slice.call(this.suggest.querySelectorAll('[data-sn-card]')) : [];
    this.live = card.querySelector('[data-sn-order-live]');
    this.button = card.querySelector('.sn-pdp-hero__add-to-bag');
    this.ctaLabel = card.querySelector('[data-sn-cta-label]');
    this.qtyInput = card.querySelector('.sn-qty__input');
    this.subRadio = card.querySelector('input[type="radio"][value="subscribe"]');
    this.oneRadio = card.querySelector('input[type="radio"][value="onetime"]');
    this.tab = card.querySelector('[data-sn-sub-tab]');
    this.microBlocked = card.querySelector('[data-sn-micro-blocked]');
    const d = card.dataset;
    this.pct = parseInt(d.pct, 10) || 0;
    this.isCombo = card.hasAttribute('data-sn-combo');
    this.currency = d.currency || '';
    this.threshold = parseInt(d.threshold, 10) || 0;
    this.thresholdText = d.thresholdText || '';
    this.added = [];
    this.base = snProductData(this.baseRows[0]);
    card.snOrder = this;
    this.bind();
    this.sync();
  }

  bind() {
    Array.prototype.forEach.call(
      this.card.querySelectorAll('input[type="radio"][name="sn-purchase-mode"]'),
      (r) => r.addEventListener('change', () => this.sync())
    );
    // The stepper and a typed quantity both fire change on the input.
    if (this.qtyInput) this.qtyInput.addEventListener('change', () => this.sync());
    this.cards.forEach((el) => el.addEventListener('click', () => this.add(el)));
    this.list.addEventListener('click', (event) => {
      const btn = event.target.closest ? event.target.closest('[data-sn-remove]') : null;
      if (!btn) return;
      const row = btn.closest('[data-sn-row]');
      const entry = this.added.filter((a) => a.row === row)[0];
      if (entry) this.remove(entry);
    });
    // Back/forward restores can bring the radios back in another state.
    window.addEventListener('pageshow', () => this.sync());
  }

  fmt(cents) {
    return snFormatMoney(cents, this.currency);
  }

  mode() {
    return snIsSubscribe(this.card) ? 'subscribe' : 'onetime';
  }

  hasSubscribe() {
    return !!this.subRadio;
  }

  quantity() {
    const q = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
    return q > 0 ? q : 1;
  }

  // Can this product share a Subscribe order with this page's product?
  compatible(data) {
    return this.hasSubscribe() &&
      isFinite(data.sub) && isFinite(this.base.sub) &&
      !!data.planText && data.planText === this.base.planText;
  }

  blockers() {
    return this.added.filter((a) => !this.compatible(a.data));
  }

  items() {
    const out = this.baseRows.map((el) => ({ el: el, role: el.dataset.role, data: snProductData(el) }));
    this.added.forEach((a) => out.push({ el: a.row, role: 'addon', data: a.data }));
    return out;
  }

  // The order's numbers for one mode, or null when that mode cannot be used.
  compute(mode) {
    const items = this.items();
    const single = items.length === 1;
    const stepper = mode === 'onetime' && single && !!this.qtyInput;
    const qty = stepper ? this.quantity() : 1;
    const units = single ? qty : items.length;
    const lines = [];
    for (let i = 0; i < items.length; i++) {
      const data = items[i].data;
      if (mode === 'subscribe') {
        if (!isFinite(data.sub)) return null;
        lines.push({ full: data.full, price: data.sub });
      } else {
        const full = data.full * (single ? qty : 1);
        let price = full;
        if (units >= 2) {
          price = single ? full - Math.floor((full * this.pct) / 100) : data.paired;
        }
        lines.push({ full: full, price: price });
      }
    }
    let subtotal = 0;
    let total = 0;
    lines.forEach((line) => { subtotal += line.full; total += line.price; });
    return {
      mode: mode,
      items: items,
      lines: lines,
      qty: qty,
      units: units,
      subtotal: subtotal,
      total: total,
      saving: subtotal - total,
      free: mode === 'subscribe' || units >= 2 || total >= this.threshold
    };
  }

  sync() {
    const blockers = this.blockers();
    const blocked = this.hasSubscribe() && blockers.length > 0;
    // Subscribe cannot be chosen while a product that cannot share the plan
    // is in the order; if it somehow is, fall back to One-time.
    if (blocked && this.mode() === 'subscribe' && this.oneRadio) this.oneRadio.checked = true;
    const calc = this.compute(this.mode());
    if (!calc) return;

    this.renderState(blocked, blockers);
    this.renderTotals();
    this.renderPanel(calc);
    this.renderCards(calc);
    this.renderButton(calc);
    // "Your order" lists the products, so it is only worth showing for two or
    // more of them (one product at any quantity needs no list), and always on a
    // combo page.
    this.panel.hidden = !(this.isCombo || calc.items.length >= 2);
  }

  // Attributes the CSS reads: an add-on is in the order (the stepper hides),
  // and Subscribe is blocked (the tab and the microcopy change).
  renderState(blocked, blockers) {
    if (this.added.length > 0) {
      this.card.setAttribute('data-sn-bundled', '');
    } else {
      this.card.removeAttribute('data-sn-bundled');
    }
    if (blocked) {
      this.card.setAttribute('data-sn-blocked', '');
    } else {
      this.card.removeAttribute('data-sn-blocked');
    }
    if (this.subRadio) this.subRadio.disabled = blocked;
    if (this.tab) {
      if (blocked) {
        this.tab.setAttribute('aria-disabled', 'true');
      } else {
        this.tab.removeAttribute('aria-disabled');
      }
    }
    if (this.microBlocked) {
      const names = blockers.map((a) => a.data.name).join(' and ');
      this.microBlocked.textContent = snFill(this.card.dataset.blockedText, { name: names });
    }
  }

  // The big total and its small line, for each mode that has a block. Both
  // modes are kept current so the CSS-only tab swap always shows real numbers.
  renderTotals() {
    ['onetime', 'subscribe'].forEach((mode) => {
      const block = this.card.querySelector('[data-sn-total="' + mode + '"]');
      if (!block) return;
      const calc = this.compute(mode);
      if (!calc) return;
      const price = block.querySelector('[data-sn-total-price]');
      const note = block.querySelector('[data-sn-total-note]');
      if (price) price.textContent = this.fmt(calc.total);
      if (note) {
        // "You save <amount> (<pct>%), free delivery" when there is a saving (the
        // percentage is the saving over the subtotal, rounded), otherwise the
        // free-delivery threshold. The strings are section settings.
        const d = this.card.dataset;
        const saving = calc.saving > 0;
        note.textContent = saving
          ? snFill(d.noteSaving, {
            saving: this.fmt(calc.saving),
            pct: Math.round((calc.saving * 100) / calc.subtotal),
            threshold: this.thresholdText
          })
          : snFill(d.noteNone, { threshold: this.thresholdText });
        note.classList.toggle('sn-pdp-purchase-card__note--saving', saving);
      }
    });
  }

  // The rows only: each product's struck full price (when discounted) and the
  // price paid. The total and the saving live above the button.
  renderPanel(calc) {
    calc.items.forEach((item, i) => {
      const line = calc.lines[i];
      const now = item.el.querySelector('[data-sn-row-now]');
      const was = item.el.querySelector('[data-sn-row-was]');
      if (now) now.textContent = this.fmt(line.price);
      if (was) {
        if (line.price < line.full) {
          was.textContent = this.fmt(line.full);
          was.hidden = false;
        } else {
          was.textContent = '';
          was.hidden = true;
        }
      }
    });
  }

  // The add-on cards: a card disappears once its product is in the order, and
  // in Subscribe the ones that cannot share the plan are faded and say so.
  renderCards(calc) {
    if (!this.suggest) return;
    const subscribing = calc.mode === 'subscribe';
    let remaining = 0;
    this.cards.forEach((el) => {
      const added = this.added.some((a) => a.card === el);
      const item = el.closest('li');
      if (item) item.hidden = added;
      if (added) return;
      remaining++;
      const data = snProductData(el);
      const usable = !subscribing || this.compatible(data);
      if (usable) {
        el.removeAttribute('aria-disabled');
      } else {
        el.setAttribute('aria-disabled', 'true');
      }
      const price = el.querySelector('[data-sn-card-price]');
      if (!price) return;
      if (!subscribing) {
        price.textContent = snFill(this.card.dataset.cardPrice, { price: this.fmt(data.paired) });
      } else if (usable) {
        price.textContent = this.fmt(data.sub) + ' ' + SN_DOT + ' ' + snCadence(data.planText).toLowerCase();
      } else {
        price.textContent = this.card.dataset.oneTimeOnly || '';
      }
    });
    this.suggest.hidden = remaining === 0;
  }

  renderButton(calc) {
    if (!this.ctaLabel) return;
    // A sold-out (or mid-request) button keeps its label.
    if (this.button && this.button.disabled) return;
    const d = this.card.dataset;
    const many = calc.units > 1;
    const template = calc.mode === 'subscribe'
      ? (many ? d.ctaSubMany : d.ctaSub)
      : (many ? d.ctaMany : d.ctaOne);
    this.ctaLabel.textContent = snFill(template, { n: calc.units, price: this.fmt(calc.total) });
  }

  announce(message) {
    if (!this.live) return;
    this.live.textContent = '';
    this.live.textContent = message;
  }

  // The row for an added product, built from its card.
  buildRow(cardEl, data) {
    const row = document.createElement('li');
    row.className = 'sn-pdp-order__row';
    row.setAttribute('data-sn-row', '');
    row.setAttribute('data-role', 'addon');

    const media = document.createElement('span');
    media.className = 'sn-pdp-order__media';
    media.setAttribute('aria-hidden', 'true');
    const img = cardEl.querySelector('img');
    if (img) {
      const clone = img.cloneNode(true);
      clone.className = 'sn-pdp-order__img';
      media.appendChild(clone);
    }

    const body = document.createElement('span');
    body.className = 'sn-pdp-order__body';
    const name = document.createElement('span');
    name.className = 'sn-pdp-order__name';
    name.textContent = data.name;
    body.appendChild(name);

    const price = document.createElement('span');
    price.className = 'sn-pdp-order__price';
    const was = document.createElement('s');
    was.className = 'sn-pdp-order__was';
    was.setAttribute('data-sn-row-was', '');
    was.hidden = true;
    const now = document.createElement('span');
    now.className = 'sn-pdp-order__now';
    now.setAttribute('data-sn-row-now', '');
    price.appendChild(was);
    price.appendChild(document.createTextNode(' '));
    price.appendChild(now);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'sn-pdp-order__remove';
    remove.setAttribute('data-sn-remove', '');
    remove.setAttribute('aria-label', 'Remove ' + data.name);
    const times = document.createElement('span');
    times.setAttribute('aria-hidden', 'true');
    times.textContent = SN_TIMES;
    remove.appendChild(times);

    row.appendChild(media);
    row.appendChild(body);
    row.appendChild(price);
    row.appendChild(remove);
    return row;
  }

  add(cardEl) {
    if (cardEl.getAttribute('aria-disabled') === 'true') return;
    if (this.added.some((a) => a.card === cardEl)) return;
    const data = snProductData(cardEl);
    const row = this.buildRow(cardEl, data);
    this.list.appendChild(row);
    this.added.push({ card: cardEl, row: row, data: data });
    this.sync();
    const calc = this.compute(this.mode());
    this.announce(data.name + ' added to your order. Total ' + this.fmt(calc ? calc.total : 0) + '.');
    const remove = row.querySelector('[data-sn-remove]');
    if (remove) remove.focus();
  }

  remove(entry) {
    if (entry.row.parentNode) entry.row.parentNode.removeChild(entry.row);
    this.added = this.added.filter((a) => a !== entry);
    this.sync();
    const calc = this.compute(this.mode());
    this.announce(entry.data.name + ' removed from your order. Total ' + this.fmt(calc ? calc.total : 0) + '.');
    // The card is back; hand focus to it so the keyboard stays where it was.
    entry.card.focus();
  }

  // The /cart/add.js items for a single product with add-ons: this product
  // and each added product, one of each, each with its own plan when
  // subscribing.
  postItems(subscribing) {
    const all = [{ id: this.base.id, planId: this.base.planId }].concat(
      this.added.map((a) => ({ id: a.data.id, planId: a.data.planId }))
    );
    return all.map((it) => {
      const item = { id: it.id, quantity: 1 };
      if (subscribing && it.planId) item.selling_plan = Number(it.planId);
      return item;
    });
  }
}

class SnPdpBuy {
  constructor(form) {
    this.form = form;
    this.card = form.querySelector('.sn-sub-toggle');
    this.button = form.querySelector('.sn-pdp-hero__add-to-bag');
    if (!this.card || !this.button) return;
    this.error = form.querySelector('[data-sn-form-error]');
    this.isCombo = this.card.hasAttribute('data-sn-combo');
    form.addEventListener('submit', (event) => this.onSubmit(event));
    // Coming back from the bag restores this page from the back/forward cache
    // with the button still disabled.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.setBusy(false);
    });
  }

  // The items to send in one request, or null when the form should simply
  // post as it always has (a single product with no add-ons in the order).
  buildItems() {
    const order = this.card.snOrder;
    const subscribing = order ? order.mode() === 'subscribe' : snIsSubscribe(this.card);
    if (this.isCombo) {
      const title = this.card.dataset.comboTitle || '';
      const inputs = this.form.querySelectorAll('[data-sn-combo-item]');
      if (!inputs.length) return null;
      return Array.prototype.map.call(inputs, (input) => {
        const item = { id: Number(input.value), quantity: 1, properties: { _combo: title } };
        if (subscribing && input.dataset.sellingPlan) {
          item.selling_plan = Number(input.dataset.sellingPlan);
        }
        return item;
      });
    }
    if (order && order.added.length > 0) return order.postItems(subscribing);
    return null;
  }

  onSubmit(event) {
    if (!window.fetch) return;
    const items = this.buildItems();
    if (!items) return;
    event.preventDefault();
    if (this.button.disabled) return;
    this.setBusy(true);
    this.showError('');
    const routes = window.routes || {};
    fetch((routes.cart_add_url || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data: data })))
      .then((result) => {
        if (result.ok) {
          // What the form does today: Shopify answers a native add with a
          // redirect to the bag page, so go there.
          window.location.assign(routes.cart_url || '/cart');
          return;
        }
        this.fail(result.data && (result.data.description || result.data.message));
      })
      .catch(() => this.fail());
  }

  fail(message) {
    this.setBusy(false);
    const generic = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
    this.showError(typeof message === 'string' && message ? message : generic);
  }

  setBusy(busy) {
    this.button.disabled = busy;
    if (busy) {
      this.button.setAttribute('aria-busy', 'true');
    } else {
      this.button.removeAttribute('aria-busy');
      if (this.card.snOrder) this.card.snOrder.sync();
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message || '';
    this.error.hidden = !message;
  }
}

/* The content column is sticky from 1024px. "Your order" and the add-on cards
   make it taller than many windows, and a sticky box taller than the window
   hides its lower part until the gallery ends. This publishes the column's
   height as --sn-pdp-col-h so the CSS can pin it by its bottom edge in that
   case (see .sn-pdp-hero__content). It only writes a custom property, which
   changes `top`, never the height, so the observer cannot loop. */
class SnStickyFit {
  constructor(el) {
    if (!window.ResizeObserver) return;
    const publish = () => el.style.setProperty('--sn-pdp-col-h', el.offsetHeight + 'px');
    publish();
    new ResizeObserver(publish).observe(el);
  }
}

function snInitPdp() {
  document.querySelectorAll('.sn-pdp-hero__content').forEach((el) => new SnStickyFit(el));
  document.querySelectorAll('[data-sn-gallery]').forEach((el) => new SnGallery(el));
  document.querySelectorAll('.sn-qty').forEach((el) => new SnQtyStepper(el));
  document.querySelectorAll('.sn-sub-toggle').forEach((el) => new SnSubToggle(el));
  document.querySelectorAll('.sn-sub-toggle').forEach((el) => new SnOrder(el));
  document.querySelectorAll('.sn-pdp-hero__form').forEach((el) => new SnPdpBuy(el));

  // Prevent browser scroll-jump when accordion summary is clicked inside
  // the sticky buy-box. Capture scroll position before the native toggle
  // fires and restore it on the next frame.
  document.querySelectorAll('.sn-pdp-hero__accordion-trigger').forEach(function (summary) {
    summary.addEventListener('click', function () {
      var scrollY = window.scrollY;
      requestAnimationFrame(function () { window.scrollTo(0, scrollY); });
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', snInitPdp);
} else {
  snInitPdp();
}
